import { Link } from 'react-router-dom';
import PremiumCard from '../ui/PremiumCard.jsx';
import ScrollReveal from './ScrollReveal.jsx';
import SectionAmbientGradient from './SectionAmbientGradient.jsx';
import SectionTopWave from './SectionTopWave.jsx';
import { assetUrl } from '../../utils/assetUrl.js';
import publicCta from '../../content/publicCta.json';
import { ChevronRight, Calendar } from 'lucide-react';

export default function NewsSection({ items, loading }) {
  return (
    <section
      id="noticias"
      className="relative isolate scroll-mt-24 overflow-hidden border-t border-white/10 pb-16 pt-12 lg:pb-24 lg:pt-14"
    >
      <SectionAmbientGradient />
      <SectionTopWave />

      <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-8">
        <ScrollReveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Asociados</p>
              <h2 className="mt-3 font-display text-3xl font-bold text-white md:text-4xl">Noticias y recordatorios</h2>
              <p className="mt-4 max-w-xl text-slate-400">
                Avisos sobre noticias, novedades, vacantes y comunicados institucionales.
              </p>
            </div>
            <Link
              to="/noticias"
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[#1acb12]/40 bg-black/20 px-4 py-2 text-sm font-semibold text-accent-lime backdrop-blur-sm transition hover:border-[#1acb12]/70 hover:bg-black/30 hover:text-white"
            >
              {publicCta.newsSeeAllLabel}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </ScrollReveal>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-72 animate-skeleton-shimmer rounded-2xl bg-white/90 ring-1 ring-slate-200"
              />
            ))}

          {!loading &&
            items?.map((n, i) => (
              <ScrollReveal key={n.id} delayMs={Math.min(i, 5) * 55} className="min-w-0">
                <Link
                  to={`/noticias/${n.id}`}
                  className="group block rounded-2xl no-underline text-inherit outline-none focus-visible:ring-2 focus-visible:ring-[#1acb12] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  aria-label={`${n.titulo}: ver detalle de la noticia`}
                >
                  <PremiumCard hover variant="light" className="flex h-full cursor-pointer flex-col">
                    <div className="relative mb-5 aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
                      {n.imagen ? (
                        <img
                          src={assetUrl(n.imagen)}
                          alt=""
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs font-medium uppercase tracking-wider text-slate-500">
                          Sin imagen
                        </div>
                      )}
                      <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
                        {n.categoria}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Calendar className="h-3.5 w-3.5 text-accent-lime" />
                        {new Date(n.fecha_publicacion).toLocaleDateString('es-CO', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                      <h3 className="mt-3 font-display text-xl font-semibold text-slate-800">{n.titulo}</h3>
                      <p className="mt-2 line-clamp-3 flex-1 text-base leading-relaxed text-slate-600">{n.resumen}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition group-hover:text-[#0d2b7f]">
                        {publicCta.newsCardLinkLabel} <ChevronRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </PremiumCard>
                </Link>
              </ScrollReveal>
            ))}
        </div>
      </div>
    </section>
  );
}
